/**
 * EvaluationsDashboard - Dashboard do sistema de avaliações técnicas
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Visão geral do sistema de avaliação
 * - Navegação entre abas (overview, my-profile, create, evolution)
 * - Tooltip dinâmico baseado na aba ativa
 * - Integração com EvolutionChart
 * - Sistema de selos (Bronze, Prata, Ouro)
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - Card, Button (shadcn/ui)
 * - EvolutionChart (gráfico de evolução)
 */

import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { useTranslation } from "../translations";
import { useSubscription } from "../hooks/useSubscription";
import UpgradeGate from "./UpgradeGate";
import EvolutionChart from "./EvolutionChart";

const EvaluationsDashboard = ({ user }) => {
  const [activeEvalTab, setActiveEvalTab] = useState('overview');
  const [showTip, setShowTip] = useState(true);
  const { t } = useTranslation();
  const { canAccess, planName, isTrial, trialExpired, daysRemaining, alertLevel } = useSubscription();

  // Tooltip dinâmico baseado na aba ativa
  const getTooltipForTab = () => {
    switch (activeEvalTab) {
      case 'overview':
        return t('tooltipEvaluationOverview');
      case 'my-profile':
        return t('tooltipEvaluationProfile');
      case 'create':
        return t('tooltipEvaluationCreate');
      case 'evolution':
        return t('tooltipEvaluationEvolution');
      default:
        return t('tooltipEvaluationOverview');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <h2 className="text-3xl font-bold mb-2">🏆 {t('technicalEvaluationSystem')}</h2>
        <p className="text-blue-100">{t('heveaTalentCertification')}</p>
      </div>

      {/* Dica de Como Usar - Explicativo baseado no Tripé */}
      {showTip && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-5 relative shadow-sm">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-3 right-3 text-purple-400 hover:text-purple-600 text-lg"
          >
            ✕
          </button>
          <div className="pr-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🏆</span>
              <h4 className="font-bold text-purple-800 text-lg">{t('howToUse')}</h4>
            </div>
            <div className="text-sm text-gray-700 space-y-2 whitespace-pre-line">
              {getTooltipForTab().split('\n\n').map((paragraph, idx) => (
                <p key={idx} className={paragraph.includes('**') ? 'font-medium' : ''}>
                  {paragraph.replace(/\*\*/g, '')}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveEvalTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEvalTab === 'overview'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('overview')}
          </button>
          <button
            onClick={() => setActiveEvalTab('my-profile')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEvalTab === 'my-profile'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('myProfile')}
          </button>
          {(user?.user_type === 'employer' || user?.user_type === 'admin' || user?.role === 'evaluator') && (
            <button
              onClick={() => setActiveEvalTab('create')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeEvalTab === 'create'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
              }`}
            >
              {t('createEvaluation')}
            </button>
          )}
          <button
            onClick={() => setActiveEvalTab('evolution')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEvalTab === 'evolution'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            📈 {t('performanceEvolution')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeEvalTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">👤</span>
              </div>
              <div>
                <h3 className="font-bold text-lg">{t('myProfessionalProfile')}</h3>
                <p className="text-gray-600 text-sm">{t('viewCertificationSeals')}</p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.href = `/worker-dashboard/${user?.id || 'demo'}`}
              className="w-full"
              variant="outline"
            >
              {t('accessDashboard')} →
            </Button>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">📝</span>
              </div>
              <div>
                <h3 className="font-bold text-lg">{t('aboutTheSystem')}</h3>
                <p className="text-gray-600 text-sm">{t('howEvaluationWorks')}</p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <span>🥉</span> <strong>{t('bronze')}:</strong> 60-74 {t('points')}
              </p>
              <p className="flex items-center gap-2">
                <span>🥈</span> <strong>{t('silver')}:</strong> 75-89 {t('points')}
              </p>
              <p className="flex items-center gap-2">
                <span>🥇</span> <strong>{t('gold')}:</strong> 90-100 {t('points')}
              </p>
            </div>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">📊</span>
              </div>
              <div>
                <h3 className="font-bold text-lg">14 {t('technicalCriteria')}</h3>
                <p className="text-gray-600 text-sm">{t('senarEmbrapaIncaperStandards')}</p>
              </div>
            </div>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>✓ {t('bleedingTechnique')} (45 pts)</li>
              <li>✓ {t('harvestPreparation')} (20 pts)</li>
              <li>✓ {t('dailyMaintenance')} (15 pts)</li>
              <li>✓ {t('chemicalApplications')} (20 pts)</li>
            </ul>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                <span className="text-3xl">🎓</span>
              </div>
              <div>
                <h3 className="font-bold text-lg">{t('educationalFeedback')}</h3>
                <p className="text-gray-600 text-sm">{t('improveContinuously')}</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Consulte os tooltips (ⓘ) em cada critério para dicas práticas baseadas em documentos oficiais INCAPER e CATI.
            </p>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/technical-references'}
              className="w-full"
            >
              Ver Referências Técnicas →
            </Button>
          </Card>
        </div>
      )}

      {activeEvalTab === 'my-profile' && (
        <Card className="p-6">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👤</div>
            <h3 className="text-xl font-bold mb-2">{t('viewFullProfile')}</h3>
            <p className="text-gray-600 mb-6">
              {t('accessDashboardToSee')}
            </p>
            <Button 
              onClick={() => window.location.href = `/worker-dashboard/${user?.id || 'demo'}`}
              size="lg"
            >
              {t('openMyDashboard')} →
            </Button>
          </div>
        </Card>
      )}

      {activeEvalTab === 'create' && (
        canAccess('give_evaluations') ? (
          <Card className="p-6">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-bold mb-2">{t('createNewEvaluation')}</h3>
              <p className="text-gray-600 mb-6">
                {t('selectWorkerToEvaluate')}
              </p>
              <Button 
                onClick={() => window.location.href = '/evaluation-form'}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              >
                {t('startEvaluation')} →
              </Button>
            </div>
          </Card>
        ) : (
          <UpgradeGate feature="evaluations" planName={planName} trialExpired={trialExpired} daysRemaining={daysRemaining} isTrial={isTrial} alertLevel={alertLevel} onUpgrade={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'plans' }))}>
            <Card className="p-6">
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-bold mb-2">{t('createNewEvaluation')}</h3>
                <p className="text-gray-600 mb-6">{t('selectWorkerToEvaluate')}</p>
              </div>
            </Card>
          </UpgradeGate>
        )
      )}

      {activeEvalTab === 'evolution' && (
        <div className="space-y-6">
          {/* Gráfico Evolutivo */}
          <EvolutionChart workerId={user?.id} />
          
          {/* Cards explicativos */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                <span>📊</span> {t('technicalSeal')}
              </h4>
              <p className="text-sm text-blue-700">
                {t('technicalSealDesc')}
              </p>
            </Card>
            
            <Card className="p-4 bg-purple-50 border-purple-200">
              <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                <span>👥</span> {t('employerEvaluations')}
              </h4>
              <p className="text-sm text-purple-700">
                {t('employerEvaluationsDesc')}
              </p>
            </Card>
          </div>
          
          {/* Explicação do sistema de pesos */}
          <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
              <span>⚖️</span> {t('weightedScore')}
            </h4>
            <p className="text-sm text-green-700">
              {t('weightedScoreDesc')}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EvaluationsDashboard;
